"use client";

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    Card,
    Box,
    Typography,
    Button,
    IconButton,
    Collapse,
    useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import BudENotes from "./BudENotes";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const formatDateLabel = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
        return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
        return "Yesterday";
    } else if (
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
    ) {
        return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } else {
        return date.toLocaleDateString("en-US", {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
    }
};

// Helper function to format time as HH:MM
const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });
};

const ChatTranscriptsWindow = ({ chatData }) => {
    const navigate = useNavigate();
    const customer = chatData?.customer || {};
    const chat_sessions = chatData?.chat_sessions || [];

    const [notesOpen, setNotesOpen] = useState(false); // Hidden by default
    const chatContainerRef = useRef(null);
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

    // ✅ Sort sessions by first message timestamp - OLDEST FIRST
    const sortedSessions = [...chat_sessions].map(session => {
        const firstMessageTimestamp = session.messages && session.messages.length > 0
            ? session.messages[0].timestamp
            : null;
        return {
            ...session,
            timestamp: firstMessageTimestamp
        };
    }).sort((a, b) =>
        new Date(a.timestamp) - new Date(b.timestamp)
    );

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, []);

    // 📄 Export as PDF
    const handleExport = () => {
        const doc = new jsPDF();
        let yOffset = 20;

        doc.setFontSize(16);
        doc.text(`${customer.name} - Chat Transcripts`, 14, yOffset);
        yOffset += 10;

        chat_sessions.forEach((session) => {
            doc.setFontSize(12);
            doc.text(
                `Session ID: ${session.session_id}`,
                14,
                yOffset
            );
            yOffset += 5;

            const rows = session.messages.map((msg) => [
                msg.sender.toUpperCase(),
                msg.message,
            ]);

            autoTable(doc, {
                head: [["Sender", "Message"]],
                body: rows,
                startY: yOffset + 2,
                theme: "grid",
                styles: { fontSize: 10, cellPadding: 2 },
                headStyles: { fillColor: [25, 118, 210] },
            });

            yOffset = doc.lastAutoTable.finalY + 10;
        });

        doc.save(`${customer.name}_Chat_Transcripts.pdf`);
    };

    return (
        <Card
            sx={{
                boxShadow: "none",
                bgcolor: "var(--whiteColor)",
                mb: "25px",
                borderRadius: "7px",
                padding: { xs: "20px", sm: "25px" },
                height: "72vh",
                display: "flex",
                flexDirection: "column",
                border: "1px solid var(--borderColor)",
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: "1px solid var(--borderColor)",
                    pb: 1,
                    flexWrap: "nowrap",
                    minWidth: 0,
                }}
            >
                {/* Left - Back */}
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate("/chat-transcripts")}
                    sx={{
                        flexShrink: 0,
                        fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.9rem" },
                        padding: { xs: "3px 6px", sm: "5px 10px" },
                    }}
                >
                    Back
                </Button>

                {/* Center - Title */}
                <Typography
                    fontWeight={600}
                    sx={{
                        flex: 1,
                        textAlign: "center",
                        fontSize: { xs: "0.85rem", sm: "1.1rem", md: "1.3rem" },
                        color: "var(--blackColor)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        mx: 1,
                    }}
                >
                    Chat
                </Typography>

                {/* Right - Actions */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexShrink: 0,
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={handleExport}
                        sx={{
                            minWidth: "70px",
                            fontSize: { xs: "0.65rem", sm: "0.8rem", md: "0.9rem" },
                            padding: { xs: "3px 6px", sm: "5px 10px" },
                            whiteSpace: "nowrap",
                        }}
                    >
                        Export <i className="material-symbols-outlined">download</i>
                    </Button>

                    <IconButton
                        color={notesOpen ? "primary" : "default"}
                        onClick={() => setNotesOpen(!notesOpen)}
                        sx={{
                            fontSize: { xs: "1rem", sm: "1.2rem" },
                        }}
                    >
                        <i className="material-symbols-outlined">clinical_notes</i>
                    </IconButton>
                </Box>
            </Box>

            {/* Chat + Notes layout */}
            <Box
                sx={{
                    display: "flex",
                    flex: 1,
                    overflow: "hidden",
                    flexDirection: isSmallScreen ? "column" : "row",
                }}
            >
                {/* Chat Body */}
                <Box
                    ref={chatContainerRef}
                    sx={{
                        flex: 1,
                        overflowY: "auto",
                        pr: 1,
                        transition: "margin 0.3s ease",
                        scrollbarWidth: "thin",
                        scrollbarColor: "rgb(180, 179, 222) transparent",
                        "&::-webkit-scrollbar": { width: "4px", height: "0.5px" },
                        "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "rgba(25, 118, 210, 0.3)",
                            borderRadius: "10px",
                        },
                        "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
                    }}
                >
                    {/* FIXED: Show sessions in chronological order (oldest first at TOP, newest last at BOTTOM) */}
                    {sortedSessions.map((session) => {
                        return (
                            <Box key={session.session_id} sx={{ mb: 3 }}>
                                {/* Date Divider - Show at the top of each session */}
                                <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.9rem" },
                                            textAlign: "center",
                                            bgcolor: "var(--whiteColor)",
                                            color: "var(--primaryColor)",
                                            borderRadius: "10px",
                                            padding: "4px 10px",
                                            display: "inline-block",
                                            border: "1px solid var(--primaryColor)",
                                        }}
                                    >
                                        {formatDateLabel(session.timestamp)}
                                    </Typography>
                                </Box>

                                {/* Messages - Show in correct order within session */}
                                <ul style={{ listStyle: "none", padding: 0 }}>
                                    {session.messages.map((msg, i) => {
                                        const isUser = msg.sender === "user";

                                        // SKIP "Session started" messages
                                        if (msg.message === "Session started" && msg.type === "button") {
                                            return null;
                                        }

                                        // SKIP blank messages
                                        if (!msg.message || msg.message.trim() === "") {
                                            return null;
                                        }

                                        return (
                                            <React.Fragment key={i}>
                                                {/* Session info - Show only for first message with FULL session ID - CENTERED */}
                                                {i === 0 && (
                                                    <Box sx={{ textAlign: "center", marginBottom: "12px", width: "100%" }}>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.75rem" },
                                                                display: "inline-block",
                                                                color: "var(--blackColor)",
                                                            }}
                                                        >
                                                            <Typography component="span" sx={{ fontWeight: 500, fontSize: "inherit", color: "var(--blackColor)" }}>
                                                                Session ID:
                                                            </Typography>{" "}
                                                            {session.session_id}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {/* Message wrapper */}
                                                <li
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "row",
                                                        alignItems: "flex-start",
                                                        marginBottom: "10px",
                                                        justifyContent: isUser ? "flex-end" : "flex-start",
                                                        width: "100%"
                                                    }}
                                                >
                                                    {/* Message content */}
                                                    <Box sx={{ maxWidth: "70%", position: "relative", paddingBottom: "16px" }}>
                                                        {/* Product suggestions - ONLY for bot messages with link and type suggestion */}
                                                        {!isUser && msg.link && msg.type === "suggestion" ? (
                                                            <Box>
                                                                <Box
                                                                    sx={{
                                                                        fontSize: "14px",
                                                                        textAlign: "left",
                                                                        bgcolor: "var(--whiteColor)",
                                                                        color: "var(--blackColor)",
                                                                        borderRadius: "10px",
                                                                        padding: "8px 12px",
                                                                        display: "inline-block",
                                                                        border: "1px solid var(--borderColor)",
                                                                        marginBottom: "12px",
                                                                        wordBreak: "break-word",
                                                                        position: "relative",
                                                                        paddingRight: "35px"
                                                                    }}
                                                                >
                                                                    <Typography sx={{ fontSize: "14px" }}>
                                                                        {msg.message}
                                                                    </Typography>
                                                                    {/* Time inside message bubble */}
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            position: "absolute",
                                                                            bottom: 4,
                                                                            right: 8,
                                                                            fontSize: "0.6rem",
                                                                            opacity: 0.6,
                                                                            color: "var(--blackColor)",
                                                                        }}
                                                                    >
                                                                        {formatTime(msg.timestamp || session.timestamp)}
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{
                                                                    display: "flex",
                                                                    flexDirection: "column",
                                                                    gap: 2,
                                                                    maxWidth: "350px",
                                                                    marginBottom: "20px"
                                                                }}>
                                                                    {(() => {
                                                                        try {
                                                                            const products = JSON.parse(msg.link);
                                                                            return products.map((product, idx) => (
                                                                                <Box
                                                                                    key={idx}
                                                                                    component="a"
                                                                                    href={product.product_url}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    sx={{
                                                                                        display: "flex",
                                                                                        gap: 1.5,
                                                                                        padding: "12px",
                                                                                        borderRadius: "8px",
                                                                                        border: "1px solid var(--borderColor)",
                                                                                        bgcolor: "var(--whiteColor)",
                                                                                        cursor: "pointer",
                                                                                        transition: "all 0.2s ease",
                                                                                        textDecoration: "none",
                                                                                        "&:hover": {
                                                                                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                                                                            transform: "translateY(-2px)"
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    {/* Product Image */}
                                                                                    <Box
                                                                                        sx={{
                                                                                            width: "80px",
                                                                                            height: "80px",
                                                                                            flexShrink: 0,
                                                                                            borderRadius: "6px",
                                                                                            overflow: "hidden",
                                                                                            bgcolor: "var(--lightColor)",
                                                                                        }}
                                                                                    >
                                                                                        <img
                                                                                            src={product.image_url}
                                                                                            alt={product.product}
                                                                                            style={{
                                                                                                width: "100%",
                                                                                                height: "100%",
                                                                                                objectFit: "cover"
                                                                                            }}
                                                                                        />
                                                                                    </Box>

                                                                                    {/* Product Info */}
                                                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                                        <Typography
                                                                                            variant="body2"
                                                                                            sx={{
                                                                                                fontWeight: 600,
                                                                                                fontSize: "13px",
                                                                                                color: "var(--blackColor)",
                                                                                                mb: 0.5,
                                                                                                overflow: "hidden",
                                                                                                textOverflow: "ellipsis",
                                                                                                whiteSpace: "nowrap"
                                                                                            }}
                                                                                        >
                                                                                            {product.product}
                                                                                        </Typography>
                                                                                        <Typography
                                                                                            variant="caption"
                                                                                            sx={{
                                                                                                fontSize: "11px",
                                                                                                color: "var(--primaryColor)",
                                                                                                fontWeight: 500,
                                                                                                mb: 0.5
                                                                                            }}
                                                                                        >
                                                                                            {product.brand || "N/A"}
                                                                                        </Typography>
                                                                                        <Typography
                                                                                            variant="caption"
                                                                                            sx={{
                                                                                                fontSize: "12px",
                                                                                                color: "var(--primaryColor)",
                                                                                                fontWeight: 700,
                                                                                                display: "block",
                                                                                                mb: 0.5
                                                                                            }}
                                                                                        >
                                                                                            ${product.price || "N/A"}
                                                                                        </Typography>
                                                                                        {product.description && (
                                                                                            <Typography
                                                                                                variant="caption"
                                                                                                sx={{
                                                                                                    fontSize: "11px",
                                                                                                    color: "var(--blackColor)",
                                                                                                    display: "block",
                                                                                                    overflow: "hidden",
                                                                                                    textOverflow: "ellipsis",
                                                                                                    whiteSpace: "nowrap"
                                                                                                }}
                                                                                            >
                                                                                                {product.description}
                                                                                            </Typography>
                                                                                        )}
                                                                                    </Box>
                                                                                </Box>
                                                                            ));
                                                                        } catch (e) {
                                                                            return <Typography sx={{ fontSize: "12px", color: "var(--blackColor)" }}>Unable to load products</Typography>;
                                                                        }
                                                                    })()}
                                                                </Box>
                                                            </Box>
                                                        ) : (
                                                            <Box
                                                                sx={{
                                                                    bgcolor: isUser ? "var(--primaryColor)" : "var(--lightColor)",
                                                                    color: isUser ? "var(--whiteColor)" : "var(--blackColor)",
                                                                    padding: "8px 12px",
                                                                    borderRadius: "10px",
                                                                    border: "1px solid var(--borderColor)",
                                                                    position: "relative",
                                                                    paddingRight: "35px"
                                                                }}
                                                            >
                                                                <Typography variant="body2">{msg.message}</Typography>
                                                                {/* Time inside message bubble */}
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        position: "absolute",
                                                                        bottom: 4,
                                                                        right: 8,
                                                                        fontSize: "0.6rem",
                                                                        opacity: 0.6,
                                                                        color: isUser ? "var(--whiteColor)" : "var(--blackColor)",
                                                                    }}
                                                                >
                                                                    {formatTime(msg.timestamp || session.timestamp)}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </li>
                                            </React.Fragment>
                                        );
                                    })}
                                </ul>
                            </Box>
                        );
                    })}
                </Box>

                {/* Collapsible Notes Panel */}
                <Collapse
                    orientation="horizontal"
                    in={notesOpen}
                    timeout={300}
                    unmountOnExit
                    sx={{
                        borderLeft: "1px solid var(--borderColor)",
                        bgcolor: "var(--lightColor)",
                        transition: "width 0.3s ease",
                    }}
                >
                    <Box
                        sx={{
                            width: isSmallScreen ? "100%" : 300,
                            height: "100%",
                            p: 2,
                        }}
                    >
                        {customer.bot_notes ? (
                            <BudENotes
                                moodTrends={customer.bot_notes.mood_trends}
                                usageSummary={customer.bot_notes.usage_summary}
                                productEngagement={customer.bot_notes.product_engagement}
                            />
                        ) : (
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                No notes available
                            </Typography>
                        )}
                    </Box>
                </Collapse>
            </Box>
        </Card>
    );
};

export default ChatTranscriptsWindow;

